import { supabase } from "@/integrations/supabase/client";
import { readStoredActiveOrgId, selectActiveOrg } from "@/lib/activeOrgSelection";
import { loadWorkspaceOrgs } from "@/lib/orgWorkspaces";

export interface AdminDestinationInput {
  /** False only when we positively established the admin has no organization. */
  hasOrg: boolean;
}

/**
 * The org admin's home surface. Every admin-side landing — post-login, the
 * `/admin` index redirect, a bound invitation claim, "Switch to org admin" —
 * resolves here so the answer lives in one place.
 */
export const ADMIN_HOME = "/admin/profile";

/**
 * Where an admin lands after authenticating.
 *
 * The profile editor, always. Until 2026-09 this branched on readiness — a
 * below-threshold profile went to the editor and a ready one to /admin/feed —
 * after measuring that 43% of orgs created since 2026-07-01 never authored a
 * single profile field when dropped onto an empty social feed. The feed has
 * since been de-emphasized as the admin home outright: the profile is the
 * surface that attracts agents, so it is the surface an admin is returned to
 * whether or not it has cleared the threshold. Readiness is no longer read
 * here at all, which also removes two queries from every admin sign-in.
 *
 * If the admin genuinely has no org they go to setup; ProtectedRoute's own org
 * check enforces the same thing on every admin route regardless.
 */
export const resolveAdminDestination = ({ hasOrg }: AdminDestinationInput): string =>
  hasOrg ? ADMIN_HOME : "/admin/setup";

interface ResolveOptions {
  // Where a plain agent-role account lands. Callers preserve their own prior
  // behavior here — AuthModal's signin flow historically lands on /dashboard,
  // while AuthCallback and ResetPasswordPage land on /explore.
  agentFallback?: string;
}

/**
 * Single source of truth for where a user goes right after authenticating.
 * Staff is checked FIRST: the founder account also carries the agent role, so an
 * agent-first check would send them to /explore instead of the Console.
 *
 * Order:
 *   1. is_staff(userId) === true -> /console/people
 *   2. admin role, with an org   -> /admin/profile
 *   3. admin role, without org   -> /admin/setup
 *   4. org editor (agent role)   -> /admin/profile
 *   5. otherwise (agent)         -> options.agentFallback ?? "/explore"
 */
export async function resolvePostLoginDestination(
  userId: string,
  options: ResolveOptions = {}
): Promise<string> {
  const agentFallback = options.agentFallback ?? "/explore";

  try {
    // is_staff(_user_id uuid default auth.uid()) — pass the resolved id explicitly.
    const { data: isStaffData, error: staffError } = await supabase.rpc(
      "is_staff",
      { _user_id: userId }
    );
    if (!staffError && isStaffData === true) {
      return "/console/people";
    }
  } catch (err) {
    console.error("Error checking staff membership for post-login redirect:", err);
  }

  const { data: roleData } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .maybeSingle();

  if (roleData?.role === "admin") {
    // Resolve through the SAME workspace list, ordering, and selection
    // useActiveOrg applies (lib/orgWorkspaces, lib/activeOrgSelection), so "has
    // an org" here means the app will actually open one — INCLUDING an org the
    // user only edits, and a managed office reached through its parent company.
    const { orgs, error: workspaceError } = await loadWorkspaceOrgs(userId);

    // Cannot tell whether they have an org. Do not send an established admin to
    // setup on a failed read; ProtectedRoute still catches a genuinely org-less
    // account.
    if (workspaceError) {
      console.error("[postLoginDestination] workspace read failed:", workspaceError);
      return resolveAdminDestination({ hasOrg: true });
    }

    const active = selectActiveOrg(orgs, readStoredActiveOrgId());
    return resolveAdminDestination({ hasOrg: active !== null && active !== undefined });
  }

  // Not a platform admin. An org editor keeps the platform `agent` role but still
  // has a workspace: send them to the profile editor (a surface they can reach)
  // rather than the agent app. Cheap single-row probe; skipped for the common
  // agent by returning as soon as it comes back empty.
  const { data: editorMemberships, error: editorError } = await supabase
    .from("organization_members")
    .select("id")
    .eq("user_id", userId)
    .eq("is_current", true)
    .eq("role", "editor")
    .limit(1);

  if (!editorError && editorMemberships && editorMemberships.length > 0) {
    return ADMIN_HOME;
  }

  return agentFallback;
}
